<template>
  <div class="page farm-depot-theme">
    <div class="banner farm-depot-banner">
      <div class="container d-flex justify-content-center"></div>
      <div class="container d-flex justify-content-center">
        <img class="banner-logo" :src="logo" alt="Farm Depot Logo" @error="onImageError">
      </div>
    </div>

    <div class="container main-content">
      <div id="swal-container"></div>

      <div class="form-container">
        <h3 class="form-title">Farm Depot Payment</h3>

        <div v-if="loading || isLoadingPrice" class="text-center my-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p>{{ isLoadingPrice ? 'Looking up price for your location...' : 'Loading payment form...' }}</p>
        </div>

        <div v-if="priceLookupError" class="alert alert-danger text-center my-4" role="alert">
          <strong>Price Not Available:</strong> {{ priceLookupError }}
          <p class="mb-0 mt-2"><small>Please contact support or try a different location/brand.</small></p>
        </div>

        <div v-if="!loading && !priceLookupError">
          <!-- Price Display Card -->
          <div v-if="foundPrice" class="price-card mb-4">
            <div class="price-card-header">
              <h4>Your Selected Price</h4>
            </div>
            <div class="price-card-body">
              <div class="price-row">
                <span class="price-label">Location ID:</span>
                <span class="price-value">{{ locationId }}</span>
              </div>
              <div class="price-row">
                <span class="price-label">Brand ID:</span>
                <span class="price-value">{{ brandId }}</span>
              </div>
              <div class="price-row total">
                <span class="price-label">Unit Price:</span>
                <span class="price-value">{{ formatCurrency(unitPrice) }}</span>
              </div>
            </div>
          </div>

          <!-- Payment Amount Input -->
          <div v-if="foundPrice" class="form-field payment-field mb-4 pb-4 border-bottom">
            <label for="payment-amount">
              Payment Amount
              <span class="required">*</span>
            </label>
            <input
                id="payment-amount"
                type="number"
                v-model.number="paymentAmount"
                required
                class="form-input"
                placeholder="Enter amount"
                @input="updateSummary"
                step="0.01"
                min="0"
                :disabled="isSubmitting" />
            <p class="help-text">Enter the amount you wish to pay</p>
          </div>

          <!-- Customer Details Section -->
          <div v-if="foundPrice" class="custom-fields-section border-t pt-4 mt-4">
            <h4 class="text-md font-semibold mb-3">Customer Details</h4>

            <div class="form-field mb-4 pb-4 border-bottom">
              <label for="customer-name">
                Full Name
                <span class="required">*</span>
              </label>
              <input
                  id="customer-name"
                  type="text"
                  v-model="customerName"
                  required
                  class="form-input"
                  placeholder="Enter your full name"
                  :disabled="isSubmitting" />
            </div>

            <div class="form-field mb-4 pb-4 border-bottom">
              <label for="customer-email">
                Email Address
                <span class="required">*</span>
              </label>
              <input
                  id="customer-email"
                  type="email"
                  v-model="customerEmail"
                  required
                  class="form-input"
                  placeholder="Enter your email address"
                  :disabled="isSubmitting" />
            </div>

            <div class="form-field mb-4 pb-4 border-bottom">
              <label for="customer-phone">
                Phone Number
              </label>
              <input
                  id="customer-phone"
                  type="tel"
                  v-model="customerPhone"
                  class="form-input"
                  placeholder="Enter your phone number"
                  :disabled="isSubmitting" />
            </div>
          </div>

          <div v-if="foundPrice && formFields.length > 0" class="custom-fields-section border-t pt-4 mt-4">
            <h4 class="text-md font-semibold mb-3">Additional Information</h4>
            <div v-for="(field, index) in formFields" :key="field.id || 'field-' + index" class="form-field mb-4 pb-4 border-bottom" v-show="shouldShowField(field)">
              <label :for="'field-input-' + index">
                {{ field.label }}<span v-if="field.required" class="required">*</span>
              </label>
              <input v-if="field.type === 'text'" type="text" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || ''" :disabled="isSubmitting"/>
              <input v-else-if="field.type === 'payment'" type="number" v-model.number="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || 'Enter amount'" step="0.01" min="0" @input="updateSummary" :disabled="isSubmitting"/>
              <input v-else-if="field.type === 'email'" type="email" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || 'Enter email address'" :disabled="isSubmitting"/>
              <input v-else-if="field.type === 'tel'" type="tel" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || 'Enter phone number'" :disabled="isSubmitting"/>
              <textarea v-else-if="field.type === 'textarea'" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || ''" :disabled="isSubmitting"></textarea>
              <select v-else-if="field.type === 'select'" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :disabled="isSubmitting">
                <option value="" disabled>-- {{ field.placeholder || 'Select an option' }} --</option>
                <option v-for="(option,oIdx) in field.options" :key="oIdx" :value="option">{{ option }}</option>
              </select>
              <p v-if="field.helpText" class="help-text">{{ field.helpText }}</p>
            </div>
          </div>

          <div class="navigation-buttons mt-5">
            <button
                type="submit"
                @click="submitForm"
                class="submit-btn farm-depot-btn"
                :disabled="isSubmitting || loading || !foundPrice || paymentAmount <= 0">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ isSubmitting ? 'Processing...' : 'Make Payment' }}
            </button>
          </div>
        </div>
      </div>

      <div class="summary-container" v-if="foundPrice">
        <div class="card sticky-top">
          <div class="card-body">
            <h3 class="card-title text-center mb-3">Order Summary</h3>
            <div class="summary-item">
              <span>Unit Price:</span>
              <span class="fw-bold">{{ formatCurrency(unitPrice) }}</span>
            </div>
            <div class="summary-item">
              <span>Payment Amount:</span>
              <span class="fw-bold">{{ formatCurrency(paymentAmount) }}</span>
            </div>
            <hr class="my-3">
            <div class="summary-item total-amount">
              <span class="fw-bold">Total:</span>
              <span class="fw-bold">{{ formatCurrency(paymentAmount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "FarmDepotPaymentView",
  data() {
    return {
      companyName: "",
      companyApiKey: null,
      loading: false,
      isSubmitting: false,
      logo: null,
      defaultImageUrl: 'https://placehold.co/300x100/2E7D32/FFF?text=Farm+Depot',

      // Price lookup state
      locationId: null,
      brandId: null,
      foundPrice: false,
      unitPrice: 0,
      priceLookupError: null,
      isLoadingPrice: false,

      // Payment form state
      paymentAmount: 0,
      customerName: '',
      customerEmail: '',
      customerPhone: '',

      // Form fields from backend
      formFields: []
    };
  },
  beforeMount() {
    this.companyName = this.$route.params.company;
    this.locationId = this.$route.query.location_id ? parseInt(this.$route.query.location_id, 10) : null;
    this.brandId = this.$route.query.brand_id ? parseInt(this.$route.query.brand_id, 10) : null;

    console.log(`[DEBUG FARM DEPOT] Company: ${this.companyName}, location_id: ${this.locationId}, brand_id: ${this.brandId}`);

    if (this.companyName) {
      this.initializePayment();
    } else {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Company identifier is missing.' });
    }
  },
  methods: {
    async initializePayment() {
      this.loading = true;
      this.isLoadingPrice = true;

      try {
        // First get company info and API key
        await this.getCompanyInfo();

        // Then lookup price
        if (this.locationId && this.brandId) {
          await this.lookupPriceByLocationBrand();
        } else {
          this.priceLookupError = "Missing location_id or brand_id parameters";
        }
      } catch (error) {
        console.error("[DEBUG FARM DEPOT] Initialization error:", error);
        this.priceLookupError = error.message || "Failed to initialize payment";
      } finally {
        this.loading = false;
        this.isLoadingPrice = false;
      }
    },

    async getCompanyInfo() {
      if (typeof ChickenLittle === 'undefined') {
        throw new Error("Application endpoint is missing.");
      }

      const apiUrl = `${ChickenLittle}Requirement/getCompanyFormByAlias?alias=${this.companyName}`;
      const { data } = await axios.get(apiUrl);

      if (!data || !data.apiKey) {
        throw new Error("Invalid company information received");
      }

      this.companyApiKey = data.apiKey;
      this.getLogo();

      // Parse the form-management fields the client has configured so we can render
      // them in the UI and submit the values back with the transaction.
      // The backend returns the field array inside a doubly-nested JSON string:
      //   { apiKey: "...", form: "{\"form\": [ ...fields... ]}" }
      if (typeof data.form === 'string' && data.form) {
        try {
          const outer = JSON.parse(data.form);
          const rawFields = Array.isArray(outer?.form) ? outer.form : [];
          this.formFields = rawFields
              .map((f, i) => this.cleanAndInitializeField(f, i))
              .filter(Boolean);
        } catch (e) {
          console.warn('[DEBUG FARM DEPOT] Could not parse form-management fields:', e);
          this.formFields = [];
        }
      } else {
        this.formFields = [];
      }
    },

    // Minimal field cleaner for the single-brand flow. Mirrors PaymentView's helper
    // but trimmed to the keys the legacy view actually uses (id, label, type, value,
    // placeholder, required, backendKey). Anything unknown is preserved as-is.
    cleanAndInitializeField(field, index) {
      if (!field || typeof field !== 'object') return null;
      const f = { ...field };
      if (!f.id) f.id = `f${index}_${Date.now()}`;
      if (!f.backendKey) {
        const base = (f.label || `field_${f.id}`).replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').substring(0, 50);
        f.backendKey = base || `field_${f.id}`;
      }
      if (f.value === undefined || f.value === null) f.value = '';
      return f;
    },

    async getLogo() {
      if (this.companyApiKey && typeof gateway !== 'undefined') {
        this.logo = gateway + `logo?apikey=${this.companyApiKey}`;
      } else {
        this.logo = this.defaultImageUrl;
      }
    },

    async lookupPriceByLocationBrand() {
      if (!this.locationId || !this.brandId) {
        this.priceLookupError = "Missing location or brand parameters";
        return;
      }

      this.isLoadingPrice = true;
      this.priceLookupError = null;

      try {
        const apiUrl = `${ChickenLittle}prices/lookup?locationId=${this.locationId}&brandId=${this.brandId}`;
        console.log(`[DEBUG FARM DEPOT] Looking up price at: ${apiUrl}`);

        const response = await axios.get(apiUrl, {
          headers: {
            'Api-Key': this.companyApiKey
          }
        });

        console.log("[DEBUG FARM DEPOT] Price lookup response:", response.data);

        if (response.data && response.data.found === false) {
          this.foundPrice = false;
          this.priceLookupError = "Price not available for this location/brand";
        } else if (response.data && response.data.price) {
          this.foundPrice = true;
          this.unitPrice = parseFloat(response.data.price) || 0;
          this.paymentAmount = this.unitPrice;
          this.priceLookupError = null;
        }
      } catch (error) {
        console.error("[DEBUG FARM DEPOT] Price lookup error:", error);
        if (error.response?.status === 404 || error.response?.data?.found === false) {
          this.foundPrice = false;
          this.priceLookupError = "Price not available for this location/brand";
        } else {
          this.foundPrice = false;
          this.priceLookupError = error.response?.data?.message || error.message || "Failed to lookup price";
        }
      } finally {
        this.isLoadingPrice = false;
      }
    },

    async submitForm() {
      // Validation
      if (this.paymentAmount <= 0) {
        await Swal.fire({ icon: 'warning', title: 'Invalid Amount', text: 'Please enter a valid payment amount.' });
        return;
      }

      if (!this.customerName || !this.customerEmail) {
        await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in all required fields.' });
        return;
      }

      this.isSubmitting = true;

      try {
        // Build additionalFields including the client's form-management values.
        // Each visible field is stored under its backendKey (sanitized) so the
        // Admin Portal's "View Details" modal can show what the customer entered.
        const additionalInfo = {
          customer_name: this.customerName,
          customer_email: this.customerEmail,
          customer_phone: this.customerPhone || '',
          location_id: this.locationId,
          brand_id: this.brandId,
          unit_price: this.unitPrice
        };
        if (Array.isArray(this.formFields)) {
          for (const field of this.formFields) {
            if (!this.shouldShowField(field)) continue;
            if (field.type === 'payment') continue; // contributes to totalAmount only
            let key = field.backendKey;
            if (!key || key === 'amount' || key === 'unit_price' || /\s/.test(key) || !/^[a-zA-Z0-9_]+$/.test(key)) {
              key = (field.label || `field_${field.id}`)
                  .replace(/\s+/g, '_')
                  .replace(/[^a-zA-Z0-9_]/g, '')
                  .substring(0, 50);
            }
            if (!key) continue;
            if (field.value === '' || field.value === null || field.value === undefined) continue;
            additionalInfo[key] = field.value;
          }
        }

        const payloadData = {
          localid: `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          apikey: this.companyApiKey,
          description: `Farm Depot Payment - Location ${this.locationId}, Brand ${this.brandId}`,
          totalAmount: this.paymentAmount,
          additionalFields: JSON.stringify(additionalInfo)
        };

        console.log("[DEBUG FARM DEPOT] Submitting payload:", payloadData);

        const transactionUrl = await this.$store.dispatch('createTransaction', payloadData);

        if (!transactionUrl || typeof transactionUrl !== 'string') {
          throw new Error('Transaction could not be initiated.');
        }

        await Swal.fire({
          icon: 'success',
          title: 'Redirecting to Payment',
          text: 'You will be redirected shortly.',
          timer: 3000,
          showConfirmButton: false,
          allowOutsideClick: false
        });

        window.location.href = transactionUrl;

      } catch (error) {
        console.error("[DEBUG FARM DEPOT] Submission error:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: error.message || 'Could not process payment. Please try again.'
        });
        this.isSubmitting = false;
      }
    },

    updateSummary() {
      this.$nextTick(() => {});
    },

    formatCurrency(value) {
      const number = Number(value);
      return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' })
        .format(isNaN(number) ? 0 : number);
    },

    onImageError(event) {
      event.target.src = this.defaultImageUrl;
    },

    shouldShowField(field) {
      if (!field.conditionalLogic?.enabled) return true;
      return true;
    }
  }
};
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f5f5f5;
  font-family: 'Inter', sans-serif;
}

.farm-depot-banner {
  background-color: #2E7D32;
  padding: 1rem 0;
  color: #fff;
  text-align: center;
}

.banner-logo {
  max-height: 80px;
  width: auto;
  border-radius: 8px;
}

.main-content {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 2rem 1rem;
  flex-grow: 1;
}

.form-container {
  flex: 2;
  min-width: 300px;
  background-color: #fff;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.summary-container {
  flex: 1;
  min-width: 250px;
}

.form-title {
  text-align: center;
  margin-bottom: 2rem;
  color: #2E7D32;
  font-weight: 600;
}

.form-field {
  margin-bottom: 1.5rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background-color: #fff;
}

.form-input:focus {
  border-color: #2E7D32;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(46, 125, 50, 0.25);
}

.form-input:disabled {
  background-color: #e9ecef;
  opacity: 0.7;
  cursor: not-allowed;
}

.required {
  color: #dc3545;
  margin-left: 0.25rem;
}

.help-text {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

/* Price Card Styling */
.price-card {
  background-color: #E8F5E9;
  border: 2px solid #2E7D32;
  border-radius: 12px;
  overflow: hidden;
}

.price-card-header {
  background-color: #2E7D32;
  color: white;
  padding: 1rem;
  text-align: center;
}

.price-card-header h4 {
  margin: 0;
  font-weight: 600;
}

.price-card-body {
  padding: 1.5rem;
}

.price-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
}

.price-row.total {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #A5D6A7;
  font-size: 1.2rem;
  font-weight: 600;
}

.price-label {
  color: #495057;
}

.price-value {
  color: #212529;
  font-weight: 500;
}

/* Alert Styling */
.alert {
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  color: #842029;
}

/* Submit Button */
.farm-depot-btn {
  background-color: #2E7D32;
  color: white;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.farm-depot-btn:hover:not(:disabled) {
  background-color: #1B5E20;
}

.farm-depot-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.65;
}

.navigation-buttons {
  text-center: center;
  margin-top: 2rem;
}

/* Spinner */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: -0.125em;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: .75s linear infinite spinner-border;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* Summary Card */
.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  background-color: #fff;
}

.card.sticky-top {
  top: 2rem;
}

.card-body {
  padding: 1.5rem;
}

.card-title {
  color: #2E7D32;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  color: #495057;
}

.summary-item span:first-child {
  margin-right: 1rem;
}

.summary-item span.fw-bold {
  color: #212529;
  text-align: right;
}

.summary-item.total-amount {
  font-size: 1.1rem;
  margin-top: 1rem;
}

.summary-item.total-amount span.fw-bold {
  color: #2E7D32;
}

hr {
  border-top: 1px solid #dee2e6;
}

/* SweetAlert Container */
#swal-container {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1060;
}

/* Visually hidden */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  .form-container, .summary-container {
    flex: none;
    width: 100%;
  }
}
</style>